import { ref } from 'vue'

/**
 * Композабл для кешування за ключем з дедуплікацією запитів.
 * Якщо для одного ключа викликано getOrFetch кілька разів до завершення — виконується один запит.
 *
 * @example
 * const { cache, getOrFetch } = useCache<boolean>()
 * const has = await getOrFetch('uk', async () => {
 *   const res = await api.check('uk')
 *   return res.length > 0
 * })
 */
export function useCache<T>() {
  const cache = ref<Record<string, T>>({})
  const pending = ref<Record<string, Promise<T>>>({})

  function getCached(key: string): T | undefined {
    return cache.value[key]
  }

  function setCached(key: string, value: T) {
    cache.value = { ...cache.value, [key]: value }
  }

  function getPending(key: string): Promise<T> | undefined {
    return pending.value[key]
  }

  function setPending(key: string, promise: Promise<T>) {
    pending.value[key] = promise
  }

  function clearPending(key: string) {
    delete pending.value[key]
  }

  /**
   * Повертає значення з кешу, або з активного запиту, або виконує fetcher, кешує результат і повертає його.
   * Fetcher не повинен пробрасывати помилки — при помилці поверніть fallback-значення.
   */
  async function getOrFetch(key: string, fetcher: () => Promise<T>): Promise<T> {
    const cached = getCached(key)
    if (cached !== undefined) return cached

    const existing = getPending(key)
    if (existing) return existing

    const promise = (async () => {
      try {
        const result = await fetcher()
        setCached(key, result)
        return result
      } finally {
        clearPending(key)
      }
    })()
    setPending(key, promise)
    return promise
  }

  return {
    cache,
    getCached,
    setCached,
    getPending,
    setPending,
    clearPending,
    getOrFetch,
  }
}
